/**
 * MetricMatrix — the beat-1 grid: pillar-grouped registry rows (catalogue
 * order: kpi_sort, Revenue trio by type_sort) × period columns. Monthly serves
 * the 19 matrix rows, weekly the 9-row set; the site slice comes from scope
 * (the §5 call fetches site=all once — switching site tabs never refetches).
 * Typed props only (CD-7): the catalogue and the resolve envelope, no numbers.
 *
 * The pillar header EXPANDS (10 Aug 2026, replacing the separate charts wall): the
 * pillar's metrics plot together above its rows, which is the only place a reader can
 * see the relationship BETWEEN them — Revenue against blended forecast against firm is
 * one story told by three rows, and a wall of one-metric cards could not tell it. The
 * grouping was always here; the charts tab flattened it away and has been retired.
 */

import React, { useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import type {
  Cadence,
  CataloguePillar,
  Cell,
  PlanType,
  ResolveResponse,
  Site,
} from '../../types/metrics'
import { type CellLookup } from './MatrixRow'
import PillarBody, { type PillarRows } from './MatrixPillarBody'
import PillarPlot from './PillarPlot'
import { periodHeading } from './cellFormat'
import { useColumnWidths, type PlotAlign } from './plotAlign'

interface MetricMatrixProps {
  pillars: CataloguePillar[]
  resolve: ResolveResponse
  cadence: Cadence
  site: Site
  onDrill?: (cell: Cell) => void // S3: threaded to each cell's drill affordance
  onTrend?: (metricId: string) => void // A2: threaded to each row's trend affordance
}

const key = (metricId: string, site: Site, period: string, plan: PlanType) =>
  `${metricId}|${site}|${period}|${plan}`

/** Matrix rows only, in catalogue order; pillars with no rows at this cadence
 * (e.g. Lean on monthly) do not render an empty header. */
function matrixGroups(pillars: CataloguePillar[], cadence: Cadence): PillarRows[] {
  return pillars
    .map((group) => ({
      pillar: group.pillar,
      metrics: group.metrics.filter((m) => m.matrix && m.cadence.includes(cadence)),
    }))
    .filter((group) => group.metrics.length > 0)
}

const TH = 'px-2.5 py-2.5 text-right font-mono text-[10px] font-medium uppercase tracking-[.08em] text-mut2 border-b border-line'

/** The columns are laid out, not left to the content, because a pillar trend has to line
 *  up with them (plotAlign): auto layout gives each period the width its own longest value
 *  wants, so December sat 44px from its own column. Fixed layout divides the space equally
 *  between the periods, and the table keeps a floor of MIN_PERIOD so a 34-week matrix
 *  scrolls rather than squashing every value to 27px. */
const LABEL_COL = 235
const MIN_PERIOD_COL = 76
/** Fixed like the label column, and for the same reason: the periods divide what is LEFT
 *  between them, so a YTD column that took a share would narrow every month by a twelfth
 *  and slide the pillar plot off them. */
const YTD_COL = 104

/** Fixed layout needs the columns declared up front: the Metric column is pinned, and the
 *  periods divide what is left between them — equally, which is what the plot relies on. */
function Columns({ periods, showYtd }: { periods: string[]; showYtd: boolean }): React.JSX.Element {
  return (
    <colgroup>
      <col style={{ width: LABEL_COL }} />
      {periods.map((period) => (
        <col key={period} />
      ))}
      {showYtd && <col style={{ width: YTD_COL }} />}
    </colgroup>
  )
}

function HeaderRow({ periods, showYtd }: { periods: string[]; showYtd: boolean }): React.JSX.Element {
  return (
    <tr>
      <th className={clsx('sticky left-0 z-[2] bg-panel text-left min-w-[190px]', TH)}>Metric</th>
      {periods.map((period) => (
        <th key={period} className={clsx('bg-panel', TH)}>
          {periodHeading(period)}
        </th>
      ))}
      {/* Last, where the client's own report puts it — the two are read side by side. */}
      {showYtd && <th className={clsx('bg-panel border-l border-line text-ink', TH)}>YTD</th>}
    </tr>
  )
}

/** One indexed pass over the envelope; the site slice is applied at read time so
 *  switching site tabs never rebuilds the map (or refetches). */
function useCellLookup(resolve: ResolveResponse, site: Site): CellLookup {
  const lookup = useMemo(() => {
    const map = new Map<string, Cell>()
    for (const cell of resolve.cells) {
      map.set(key(cell.metric_id, cell.site, cell.period, cell.plan_type), cell)
    }
    return map
  }, [resolve])
  return (metricId, period, plan) => lookup.get(key(metricId, site, period, plan)) ?? null
}

/** Which pillars are expanded. Local state, not the URL: expansion is a reading posture
 *  rather than a view worth linking to, and the scope params already have two writers —
 *  a third is how the capacity surface got a race. */
function useExpandedPillars() {
  const [open, setOpen] = useState<Set<string>>(new Set())
  return {
    isOpen: (pillar: string) => open.has(pillar),
    toggle: (pillar: string) =>
      setOpen((current) => {
        const next = new Set(current)
        if (!next.delete(pillar)) next.add(pillar)
        return next
      }),
  }
}

/** One tbody per pillar that has rows at this cadence, each carrying its own plot. */
function PillarBodies({
  groups,
  matrix,
  align,
  expanded,
  cellFor,
  showYtd,
}: {
  groups: PillarRows[]
  matrix: MetricMatrixProps
  align: PlotAlign | null
  expanded: ReturnType<typeof useExpandedPillars>
  cellFor: CellLookup
  showYtd: boolean
}): React.JSX.Element {
  const { resolve, cadence, site, onDrill, onTrend } = matrix
  return (
    <>
      {groups.map((group) => (
        <PillarBody
          key={group.pillar}
          group={group}
          periods={resolve.periods}
          cellFor={cellFor}
          open={expanded.isOpen(group.pillar)}
          onToggle={() => expanded.toggle(group.pillar)}
          plot={<PillarPlot metrics={group.metrics} resolve={resolve} site={site} cadence={cadence} align={align} />}
          showYtd={showYtd}
          onDrill={onDrill}
          onTrend={onTrend}
        />
      ))}
    </>
  )
}

export default function MetricMatrix(props: MetricMatrixProps): React.JSX.Element {
  const { pillars, resolve, cadence, site } = props
  const cellFor = useCellLookup(resolve, site)
  const expanded = useExpandedPillars()
  const table = useRef<HTMLTableElement>(null)
  const align = useColumnWidths(table, resolve.periods.length)
  // Monthly only: the weekly master serves no YTD_Value at all (§5), so the column would be
  // a row of dashes pretending to be a summary.
  const showYtd = cadence === 'monthly'
  const minWidth = LABEL_COL + resolve.periods.length * MIN_PERIOD_COL + (showYtd ? YTD_COL : 0)

  return (
    <div className="cx-scroll overflow-x-auto rounded-xl border border-line bg-panel">
      {/* width fills the panel when the periods fit; minWidth scrolls them when they do not. */}
      <table
        ref={table}
        data-testid="metric-matrix"
        style={{ width: '100%', minWidth }}
        className="table-fixed border-collapse text-xs"
      >
        <Columns periods={resolve.periods} showYtd={showYtd} />
        <thead>
          <HeaderRow periods={resolve.periods} showYtd={showYtd} />
        </thead>
        <PillarBodies
          groups={matrixGroups(pillars, cadence)}
          matrix={props}
          align={align}
          expanded={expanded}
          cellFor={cellFor}
          showYtd={showYtd}
        />
      </table>
    </div>
  )
}
